import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";

const AuthLayout = () => import("@/layouts/AuthLayout.vue");
const DashboardLayout = () => import("@/layouts/DashboardLayout.vue");
const SettingsLayout = () => import("@/layouts/SettingsLayout.vue");

const routes: RouteRecordRaw[] = [
  // Public / Auth routes
  {
    path: "/",
    component: AuthLayout,
    children: [
      {
        path: "",
        name: "home",
        component: () => import("@/pages/HomePage.vue"),
      },
      {
        path: "login",
        name: "login",
        component: () => import("@/pages/auth/LoginPage.vue"),
      },
      {
        path: "register",
        name: "register",
        component: () => import("@/pages/auth/RegisterPage.vue"),
      },
      {
        path: "forgot-password",
        name: "forgot-password",
        component: () => import("@/pages/auth/ForgotPasswordPage.vue"),
      },
      {
        path: "verify-email",
        name: "verify-email",
        component: () => import("@/pages/auth/VerifyEmailPage.vue"),
      },
      {
        path: "reset-password",
        name: "reset-password",
        component: () => import("@/pages/auth/ResetPasswordPage.vue"),
      },
      {
        path: "auth/callback",
        name: "oauth-callback",
        component: () => import("@/pages/auth/OAuthCallbackPage.vue"),
      },
      {
        path: "auth/dzid/callback",
        name: "dzid-callback",
        component: () => import("@/pages/auth/AuthDzidCallbackPage.vue"),
      },
    ],
  },
  // Protected dashboard routes
  {
    path: "/",
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "dashboard",
        name: "dashboard",
        component: () => import("@/pages/dashboard/DashboardPage.vue"),
      },
      {
        path: "projects",
        name: "projects",
        component: () => import("@/pages/projects/ProjectsPage.vue"),
      },
      {
        path: "projects/:id",
        name: "project-detail",
        component: () => import("@/pages/projects/ProjectDetailPage.vue"),
      },
      {
        path: "urls",
        name: "urls",
        component: () => import("@/pages/urls/UrlsPage.vue"),
      },
      {
        path: "urls/:id",
        name: "url-detail",
        component: () => import("@/pages/urls/UrlDetailPage.vue"),
      },
      {
        path: "alerts",
        name: "alerts",
        component: () => import("@/pages/AlertsPage.vue"),
      },
      {
        path: "issues",
        name: "issues",
        component: () => import("@/pages/IssuesPage.vue"),
      },
      {
        path: "cwv",
        name: "cwv",
        component: () => import("@/pages/CwvPage.vue"),
      },
      {
        path: "diff",
        name: "diff",
        component: () => import("@/pages/DiffPage.vue"),
      },
      {
        path: "reports",
        name: "reports",
        component: () => import("@/pages/ReportsPage.vue"),
      },
      {
        path: "notifications",
        name: "notifications",
        component: () => import("@/pages/NotificationsPage.vue"),
      },
      {
        path: "onboarding",
        name: "onboarding",
        component: () => import("@/pages/OnboardingPage.vue"),
      },
      {
        path: "urls/:id/results/:resultId",
        name: "url-result-detail",
        component: () => import("@/pages/urls/UrlResultDetailPage.vue"),
        meta: { requiresAuth: true },
      },
      // Settings
      {
        path: "settings",
        component: SettingsLayout,
        children: [
          { path: "", redirect: { name: "settings-profile" } },
          {
            path: "profile",
            name: "settings-profile",
            component: () => import("@/pages/settings/ProfilePage.vue"),
          },
          {
            path: "organization",
            name: "settings-organization",
            component: () => import("@/pages/settings/OrganizationPage.vue"),
          },
          {
            path: "team",
            name: "settings-team",
            component: () => import("@/pages/settings/TeamPage.vue"),
          },
          {
            path: "billing",
            name: "settings-billing",
            component: () => import("@/pages/settings/BillingPage.vue"),
          },
        ],
      },
    ],
  },
  // Catch-all — must be last
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/pages/NotFoundPage.vue"),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guard for auth
router.beforeEach(async (to) => {
  if (to.meta.requiresAuth) {
    const { useAuthStore } = await import("@/stores/auth");
    const authStore = useAuthStore();

    // Wait for the eager initial fetch to complete
    await authStore.initialFetch;

    if (!authStore.isAuthenticated) {
      return { name: "login", query: { redirect: to.fullPath } };
    }
  }
});
